import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { Badge, Card, Container } from '@/design-system/primitives'
import { useEventContent } from '@/features/event/api'
import { RubricPage } from '@/features/event/components/RubricPage'
import type { PublicEvent } from '@/features/event/types'
import { readBooleanSetting } from '@/features/event/types'
import { QuantityStepper } from '@/features/tickets/components'
import { ApiError, apiClient } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import { isInternationalNumber, looksLikeEmail } from '@/lib/phone'

import {
  usePublicDrinks,
  type PublicCompositionItem,
  type PublicDrinkCatalog,
  type PublicTableType,
} from './api'

/**
 * « Personnaliser cette table ».
 *
 * Des clients réservaient une table telle qu'affichée, puis demandaient à en
 * changer la composition — une bouteille en moins, un rhum à la place du
 * whisky. On part donc de LA table affichée (son type, sa formule, ses
 * bouteilles) : le client retire, change une quantité, ajoute une boisson de
 * la carte, donne le nombre de convives, ses coordonnées et les prénoms de
 * ses amis. L'équipe fixe ensuite le prix.
 *
 * **Aucun prix calculé n'est affiché pour la table modifiée.** Retirer une
 * bouteille d'une table à prix fixe ne baisse pas mécaniquement son prix, et
 * un chiffre « indicatif » que l'équipe démentirait ensuite ferait plus de
 * mal que de bien. On montre le prix de la table telle qu'affichée, et on dit
 * clairement que celui de la table personnalisée sera confirmé par l'équipe.
 */
export function TableCustomizePage() {
  const { t, i18n } = useTranslation()
  return (
    <RubricPage title={t('customize.title')}>
      {(event) => <CustomizeBody event={event} locale={i18n.language} />}
    </RubricPage>
  )
}

type ContactField = 'first_name' | 'last_name' | 'email' | 'whatsapp'

interface Line {
  drink_item_id: string
  label: string
  base: number
  quantity: number
}

function CustomizeBody({ event, locale }: { event: PublicEvent; locale: string }) {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const drinksQuery = usePublicDrinks(event.slug)
  const content = useEventContent(event.slug)
  const intro = (content.data ?? []).find((row) => row.key === 'tables.custom_intro')
  const allowed = readBooleanSetting(event.settings ?? {}, 'tables.allow_customization', true)

  const catalog = drinksQuery.data
  const tableType = catalog?.table_types.find((row) => row.id === params.get('table'))

  if (drinksQuery.isPending) {
    return (
      <Container className="py-10">
        <p className="text-content-muted">{t('state.loading')}</p>
      </Container>
    )
  }

  if (!catalog || !tableType || !allowed || tableType.availability === 'sold_out') {
    const reason = !allowed
      ? t('customize.closed')
      : tableType?.availability === 'sold_out'
        ? t('customize.soldOut')
        : t('customize.notFound')
    return (
      <Container className="py-10">
        <p role="status" className="max-w-2xl text-content-secondary">
          {reason}
        </p>
        <Link to="/carte#tables" className={buttonClasses({ variant: 'outline', className: 'mt-6' })}>
          {t('customize.backToTables')}
        </Link>
      </Container>
    )
  }

  return (
    <Customizer
      // Une autre table, une autre formule : on repart de zéro.
      key={`${tableType.id}:${params.get('package') ?? ''}`}
      event={event}
      catalog={catalog}
      tableType={tableType}
      initialPackageId={params.get('package')}
      locale={locale}
      intro={intro?.body}
    />
  )
}

function baseOf(tableType: PublicTableType, packageId: string | null): PublicCompositionItem[] {
  const chosen = tableType.packages.find((row) => row.id === packageId)
  return chosen ? chosen.items : tableType.included_items
}

function Customizer({
  event,
  catalog,
  tableType,
  initialPackageId,
  locale,
  intro,
}: {
  event: PublicEvent
  catalog: PublicDrinkCatalog
  tableType: PublicTableType
  initialPackageId: string | null
  locale: string
  intro: string | undefined
}) {
  const { t, i18n } = useTranslation()
  const [packageId, setPackageId] = useState<string | null>(
    tableType.packages.some((row) => row.id === initialPackageId) ? initialPackageId : null,
  )
  const base = baseOf(tableType, packageId)
  const [lines, setLines] = useState<Line[]>(() => linesFrom(base))
  const [partySize, setPartySize] = useState(
    Math.min(Math.max(4, tableType.capacity_min), tableType.capacity_max),
  )
  const [notes, setNotes] = useState('')
  const [search, setSearch] = useState('')
  const [contact, setContact] = useState({ first_name: '', last_name: '', email: '', whatsapp: '' })
  const [guests, setGuests] = useState<string[]>([])
  const [invalid, setInvalid] = useState<Partial<Record<ContactField, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [nextStep, setNextStep] = useState<string | null>(null)

  // Changer de formule, c'est repartir de SA composition : mêler les
  // bouteilles de deux formules ne ressemblerait à aucune table affichée.
  function choosePackage(next: string | null) {
    setPackageId(next)
    setLines(linesFrom(baseOf(tableType, next)))
  }

  const chosenPackage = tableType.packages.find((row) => row.id === packageId) ?? null
  const shownPrice = chosenPackage ? chosenPackage.package_price_minor : tableType.base_price_minor
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency: tableType.currency }, locale)

  const changed = lines.some((line) => line.quantity !== line.base)
  const guestSeats = Math.max(partySize - 1, 0)

  const drinksById = useMemo(
    () => new Map(catalog.drinks.map((drink) => [drink.id, drink])),
    [catalog.drinks],
  )
  const taken = new Set(lines.map((line) => line.drink_item_id))
  const needle = search.trim().toLocaleLowerCase(i18n.language)
  const groups = useMemo(() => {
    const categories = new Map(catalog.categories.map((row) => [row.id, row.name]))
    const found: { label: string; items: PublicDrinkCatalog['drinks'] }[] = []
    for (const drink of catalog.drinks) {
      if (taken.has(drink.id)) continue
      const category = (drink.category_id && categories.get(drink.category_id)) || t('customize.otherDrinks')
      const haystack = `${drink.name} ${drink.brand ?? ''} ${category}`.toLocaleLowerCase(i18n.language)
      if (needle && !haystack.includes(needle)) continue
      const group = found.find((entry) => entry.label === category)
      if (group) group.items.push(drink)
      else found.push({ label: category, items: [drink] })
    }
    return found
    // `taken` se recalcule à chaque rendu ; la liste suit `lines`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, needle, lines, i18n.language, t])

  // Un champ corrigé perd son message aussitôt (comme au tunnel).
  useEffect(() => {
    setInvalid((current) => {
      if (Object.keys(current).length === 0) return current
      const now = check()
      const kept: Partial<Record<ContactField, string>> = {}
      for (const key of Object.keys(current) as ContactField[]) if (now[key]) kept[key] = now[key]
      return kept
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact])

  function check(): Partial<Record<ContactField, string>> {
    const found: Partial<Record<ContactField, string>> = {}
    if (!contact.first_name.trim()) found.first_name = t('ticketing.validation.firstName')
    if (!contact.last_name.trim()) found.last_name = t('ticketing.validation.lastName')
    if (!looksLikeEmail(contact.email)) found.email = t('ticketing.validation.email')
    if (contact.whatsapp.trim() && !isInternationalNumber(contact.whatsapp))
      found.whatsapp = t('ticketing.validation.whatsapp')
    return found
  }

  function setQuantity(drinkId: string, quantity: number) {
    setLines((current) =>
      current
        .map((line) => (line.drink_item_id === drinkId ? { ...line, quantity } : line))
        // Une boisson ajoutée puis ramenée à zéro n'a plus rien à dire : elle
        // quitte la liste. Une bouteille de la table d'origine y reste, « retirée ».
        .filter((line) => line.base > 0 || line.quantity > 0),
    )
  }

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault()
    setError(null)
    const problems = check()
    setInvalid(problems)
    if (Object.keys(problems).length > 0) {
      setError(t('ticketing.validation.summary'))
      return
    }
    setState('sending')
    try {
      // La suite (« nous revenons vers vous avec le prix… ») est un texte
      // réécrivable en back-office : c'est le serveur qui la donne.
      const body = {
        table_type_id: tableType.id,
        package_id: packageId,
        party_size: partySize,
        items: lines.map((line) => ({ drink_item_id: line.drink_item_id, quantity: line.quantity })),
        notes: notes.trim() || null,
        first_name: contact.first_name.trim(),
        last_name: contact.last_name.trim(),
        email: contact.email.trim(),
        whatsapp: contact.whatsapp.trim() || null,
        locale: i18n.language,
        guest_names: guests
          .slice(0, guestSeats)
          .map((name) => name.trim())
          .filter(Boolean),
      }
      const sent = await apiClient.post<{ message?: string }>(
        `/public/events/${event.slug}/table-customizations`,
        body,
      )
      setNextStep(sent?.message?.trim() || null)
      setState('sent')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (cause) {
      setState('idle')
      setError(describe(cause, t))
    }
  }

  const tableName = chosenPackage ? `${tableType.name} · ${chosenPackage.name}` : tableType.name

  if (state === 'sent') {
    return (
      <Container className="py-10">
        <Card className="max-w-2xl border-state-success/40">
          <h2 className="font-display text-heading-lg text-content-primary">{t('customize.sentTitle')}</h2>
          <p className="mt-2 text-content-secondary">
            {t('customize.sentBody', { table: tableName, email: contact.email.trim() })}
          </p>
          <ChangeList lines={lines} className="mt-4" />
          <p className="mt-4 whitespace-pre-line text-sm text-content-muted">
            {nextStep ?? t('customize.sentNext')}
          </p>
          <Link to="/carte#tables" className={buttonClasses({ variant: 'outline', className: 'mt-6' })}>
            {t('customize.backToTables')}
          </Link>
        </Card>
      </Container>
    )
  }

  return (
    <Container className="py-8">
      <form noValidate onSubmit={(formEvent) => void submit(formEvent)} className="max-w-3xl space-y-8">
        {intro ? <p className="whitespace-pre-line text-content-secondary">{intro}</p> : null}

        {/* La table de départ : ce qu'on modifie, et son prix tel qu'affiché. */}
        <section aria-labelledby="customize-table">
          <p className="overline text-content-muted">{t('customize.startFrom')}</p>
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 id="customize-table" className="font-display text-heading-md text-content-primary">
              {tableType.name}
            </h2>
            <span className="tabular-nums text-champagne-300">
              {t('customize.shownPrice', { price: money(shownPrice) })}
            </span>
          </div>
          {tableType.packages.length > 0 ? (
            <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t('tables.package')}>
              <PackageChoice selected={packageId === null} onClick={() => choosePackage(null)}>
                {t('customize.asPresented')}
              </PackageChoice>
              {tableType.packages.map((row) => (
                <PackageChoice
                  key={row.id}
                  selected={packageId === row.id}
                  onClick={() => choosePackage(row.id)}
                >
                  {row.name}
                </PackageChoice>
              ))}
            </div>
          ) : null}
          <p className="mt-3 rounded-md border border-champagne-400/40 bg-bg-overlay px-4 py-3 text-sm text-content-primary">
            {t('customize.priceConfirmed')}
          </p>
        </section>

        {/* La composition : chaque bouteille de la table, puis les ajouts. */}
        <section aria-labelledby="customize-bottles">
          <h3 id="customize-bottles" className="overline text-content-muted">
            {t('customize.bottles')}
          </h3>
          {lines.length === 0 ? (
            <p className="mt-2 text-sm text-content-muted">{t('customize.noBottles')}</p>
          ) : (
            <ul className="mt-3 divide-y divide-line-subtle rounded-lg border border-line-subtle">
              {lines.map((line) => {
                const change = changeOf(line)
                return (
                  <li
                    key={line.drink_item_id}
                    className={cn(
                      'flex flex-wrap items-center justify-between gap-3 px-3 py-3 sm:px-4',
                      change === 'removed' && 'opacity-70',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'break-words text-content-primary',
                          change === 'removed' && 'line-through',
                        )}
                      >
                        {line.label}
                      </p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-2 text-caption text-content-muted">
                        {line.base > 0 ? t('customize.inTable', { count: line.base }) : null}
                        {change !== 'unchanged' ? (
                          <Badge tone={change === 'removed' ? 'ember' : 'champagne'}>
                            {t(`customize.change.${change}`)}
                          </Badge>
                        ) : null}
                        {line.base > 0 && change !== 'unchanged' ? (
                          <button
                            type="button"
                            onClick={() => setQuantity(line.drink_item_id, line.base)}
                            className="min-h-9 underline"
                          >
                            {t('customize.restore')}
                          </button>
                        ) : null}
                      </p>
                    </div>
                    <QuantityStepper
                      value={line.quantity}
                      onChange={(value) => setQuantity(line.drink_item_id, value)}
                      max={availableMax(line, drinksById.get(line.drink_item_id))}
                      label={line.label}
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* Ajouter une boisson de la carte, rangée par catégorie. */}
        <section aria-labelledby="customize-add">
          <h3 id="customize-add" className="overline text-content-muted">
            {t('customize.add')}
          </h3>
          <input
            type="search"
            value={search}
            onChange={(changeEvent) => setSearch(changeEvent.target.value)}
            placeholder={t('customize.search')}
            aria-label={t('customize.search')}
            className={cn(INPUT, 'mt-3')}
          />
          <div className="mt-3 max-h-80 space-y-4 overflow-y-auto pr-1">
            {groups.length === 0 ? (
              <p className="text-sm text-content-muted">{t('customize.noDrinkFound')}</p>
            ) : (
              groups.map((group) => (
                <div key={group.label}>
                  <p className="text-caption text-content-muted">{group.label}</p>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {group.items.map((drink) => {
                      const soldOut = drink.availability === 'sold_out'
                      return (
                        <li key={drink.id}>
                          <button
                            type="button"
                            disabled={soldOut}
                            onClick={() =>
                              setLines((current) => [
                                ...current,
                                { drink_item_id: drink.id, label: drink.name, base: 0, quantity: 1 },
                              ])
                            }
                            className="min-h-11 rounded-md border border-line px-3 py-2 text-left text-sm text-content-primary transition hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            + {drink.name}
                            {drink.size_label ? (
                              <span className="text-content-muted"> · {drink.size_label}</span>
                            ) : null}
                            {soldOut ? (
                              <span className="text-content-muted"> · {t('tables.soldOut')}</span>
                            ) : null}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </section>

        <section aria-labelledby="customize-party">
          <h3 id="customize-party" className="overline text-content-muted">
            {t('customize.party')}
          </h3>
          {tableType.capacity_min === tableType.capacity_max ? (
            <p className="mt-3 text-sm text-content-secondary">
              {t('tables.seatsFixed', { count: tableType.capacity_max })}
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <QuantityStepper
                value={partySize}
                onChange={(value) =>
                  setPartySize(Math.min(Math.max(value, tableType.capacity_min), tableType.capacity_max))
                }
                max={tableType.capacity_max}
                label={t('customize.party')}
              />
              <span className="text-sm text-content-secondary">
                {t('tables.capacity', { min: tableType.capacity_min, max: tableType.capacity_max })}
              </span>
            </div>
          )}
        </section>

        <label className="block">
          <span className="overline text-content-muted">{t('customize.notes')}</span>
          <textarea
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(changeEvent) => setNotes(changeEvent.target.value)}
            placeholder={t('customize.notesPlaceholder')}
            className={cn(INPUT, 'mt-2 h-auto py-2')}
          />
        </label>

        {/* Les coordonnées : celui qui reçoit le prix, le lien et le QR. */}
        <section aria-labelledby="customize-contact" className="space-y-4">
          <h3 id="customize-contact" className="overline text-content-muted">
            {t('ticketing.yourDetails')}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <ContactInput
              label={t('ticketing.firstName')}
              autoComplete="given-name"
              value={contact.first_name}
              error={invalid.first_name}
              onChange={(value) => setContact({ ...contact, first_name: value })}
            />
            <ContactInput
              label={t('ticketing.lastName')}
              autoComplete="family-name"
              value={contact.last_name}
              error={invalid.last_name}
              onChange={(value) => setContact({ ...contact, last_name: value })}
            />
          </div>
          <ContactInput
            label={t('ticketing.email')}
            type="email"
            autoComplete="email"
            value={contact.email}
            error={invalid.email}
            onChange={(value) => setContact({ ...contact, email: value })}
          />
          <ContactInput
            label={`${t('ticketing.whatsapp')} (${t('ticketing.optional')})`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+49 170 1234567"
            value={contact.whatsapp}
            error={invalid.whatsapp}
            help={t('ticketing.whatsappHelp')}
            onChange={(value) => setContact({ ...contact, whatsapp: value })}
          />
          {guestSeats > 0 ? (
            <fieldset>
              <legend className="overline text-content-muted">
                {t('customize.guests', { count: guestSeats })}
              </legend>
              <p className="mt-1 text-xs text-content-muted">{t('customize.guestsHint')}</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Array.from({ length: guestSeats }, (_, index) => (
                  <input
                    key={index}
                    aria-label={t('customize.guestName', { number: index + 2 })}
                    placeholder={t('customize.guestName', { number: index + 2 })}
                    autoComplete="off"
                    maxLength={120}
                    value={guests[index] ?? ''}
                    onChange={(changeEvent) =>
                      setGuests((current) => {
                        const next = [...current]
                        next[index] = changeEvent.target.value
                        return next
                      })
                    }
                    className={INPUT}
                  />
                ))}
              </div>
            </fieldset>
          ) : null}
        </section>

        {catalog.admission_notice && !tableType.includes_admission ? (
          <p className="rounded-md border border-line-subtle bg-bg-overlay p-4 text-sm text-content-secondary">
            ⚠️ {catalog.admission_notice}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-md border border-state-danger/40 bg-bg-overlay p-4 text-sm text-content-primary">
            {error}
          </p>
        ) : null}

        <div className="space-y-3">
          {!changed && !notes.trim() ? (
            <p className="text-sm text-content-muted">
              {t('customize.unchanged')}{' '}
              <Link to={`/billets?table=${tableType.id}`} className="text-champagne-300 underline">
                {t('tables.book')}
              </Link>
            </p>
          ) : null}
          <Button
            type="submit"
            className="w-full sm:w-auto"
            disabled={state === 'sending' || (!changed && !notes.trim())}
          >
            {state === 'sending' ? t('customize.sending') : t('customize.submit')}
          </Button>
          <p className="text-xs text-content-muted">{t('customize.noPaymentYet')}</p>
        </div>
      </form>
    </Container>
  )
}

function linesFrom(base: PublicCompositionItem[]): Line[] {
  return base.map((item) => ({
    drink_item_id: item.drink_item_id,
    label: item.label,
    base: item.quantity,
    quantity: item.quantity,
  }))
}

function changeOf(line: Line): 'unchanged' | 'added' | 'removed' | 'changed' {
  if (line.quantity === line.base) return 'unchanged'
  if (line.base === 0) return 'added'
  if (line.quantity === 0) return 'removed'
  return 'changed'
}

/**
 * Le plafond du sélecteur : ce qu'on peut encore ajouter. Une boisson épuisée
 * ne monte pas au-delà de la table d'origine ; le serveur revérifie le stock.
 */
function availableMax(line: Line, drink: PublicDrinkCatalog['drinks'][number] | undefined): number {
  if (drink?.availability === 'sold_out') return line.base
  return 100
}

function describe(cause: unknown, t: (key: string) => string): string {
  if (!(cause instanceof ApiError)) return t('customize.failed')
  const known: Record<string, string> = {
    DRINK_UNAVAILABLE: 'customize.errors.drinkUnavailable',
    DRINK_NOT_FOUND: 'customize.errors.drinkUnavailable',
    TABLE_SOLD_OUT: 'customize.soldOut',
    TABLE_TYPE_CAPACITY: 'customize.errors.capacity',
    CUSTOMIZATION_UNCHANGED: 'customize.unchanged',
    TABLE_CUSTOMIZATION_CLOSED: 'customize.closed',
    RATE_LIMITED: 'customize.errors.rateLimited',
    VALIDATION_ERROR: 'ticketing.validation.summary',
  }
  const key = known[cause.code]
  return key ? t(key) : t('customize.failed')
}

function ChangeList({ lines, className }: { lines: Line[]; className?: string }) {
  const { t } = useTranslation()
  const changed = lines.filter((line) => changeOf(line) !== 'unchanged')
  if (changed.length === 0) return null
  return (
    <ul className={cn('space-y-1 text-sm text-content-primary', className)}>
      {changed.map((line) => (
        <li key={line.drink_item_id}>
          {t(`customize.summary.${changeOf(line)}`, {
            drink: line.label,
            count: line.quantity,
            before: line.base,
          })}
        </li>
      ))}
    </ul>
  )
}

function PackageChoice({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'min-h-11 rounded-md border px-3 py-2 text-sm transition',
        selected
          ? 'border-ember-400 text-content-primary'
          : 'border-line-subtle text-content-secondary hover:border-line-strong',
      )}
    >
      {children}
    </button>
  )
}

function ContactInput({
  label,
  value,
  onChange,
  error,
  help,
  ...input
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error: string | undefined
  help?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <div>
      <label className="block">
        <span className="overline text-content-muted">{label}</span>
        <input
          {...input}
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(changeEvent) => onChange(changeEvent.target.value)}
          className={cn(INPUT, 'mt-2', error && 'border-state-danger')}
        />
      </label>
      {error ? (
        <p role="alert" className="mt-1.5 text-sm text-state-danger">
          {error}
        </p>
      ) : help ? (
        <p className="mt-1.5 text-xs text-content-muted">{help}</p>
      ) : null}
    </div>
  )
}

const INPUT =
  'h-11 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-base text-content-primary sm:text-sm'
